import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { decryptPasswordVault, encryptPasswordVault, type StoredPasswordVault } from './passwordVault';
import type {
  BrowserBookmark,
  BrowserHistoryEntry,
  BrowserSavedPassword,
  BrowserTabState,
  BrowserPageTheme,
} from './types';

const WEB_BROWSER_SETTINGS_APP_ID = 'web-browser';
const BROWSER_DATA_LOCAL_STORAGE_KEY = 'navigator.browser-data';
const MAX_HISTORY_ITEMS = 250;

type StoredPasswordVaultPayload = {
  credentials: BrowserSavedPassword[];
};

type BrowserStoredData = {
  pageTheme?: BrowserPageTheme;
  bookmarks?: BrowserBookmark[];
  history?: BrowserHistoryEntry[];
  passwordVault?: StoredPasswordVault | null;
};

const isHttpUrl = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const getOrigin = (value: string) => {
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return null;
    }
    return url.origin;
  } catch {
    return null;
  }
};

const getHostname = (value: string) => {
  try {
    return new URL(value).hostname;
  } catch {
    return value;
  }
};

const readFallbackData = (): BrowserStoredData => {
  try {
    const raw = localStorage.getItem(BROWSER_DATA_LOCAL_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as BrowserStoredData;
    return parsed || {};
  } catch {
    return {};
  }
};

export const useBrowserData = ({
  activeTab,
  privateMode,
  windowId,
}: {
  activeTab: BrowserTabState | null;
  privateMode: boolean;
  windowId: string;
}) => {
  const settingsApi = window.electronAPI;
  const browserApi = window.electronAPI?.browser;
  const [isLoaded, setIsLoaded] = useState(false);
  const [bookmarks, setBookmarks] = useState<BrowserBookmark[]>([]);
  const [historyEntries, setHistoryEntries] = useState<BrowserHistoryEntry[]>([]);
  const [storedVault, setStoredVault] = useState<StoredPasswordVault | null>(null);
  const [savedPasswords, setSavedPasswords] = useState<BrowserSavedPassword[]>([]);
  const [isPasswordVaultUnlocked, setIsPasswordVaultUnlocked] = useState(false);
  const masterPasswordRef = useRef<string | null>(null);
  const lastRecordedHistoryByTabRef = useRef<Record<string, string>>({});

  const persistData = useCallback(async (updater: (current: BrowserStoredData) => BrowserStoredData) => {
    const fallback = readFallbackData();
    let current = fallback;

    try {
      current = ((await settingsApi?.loadSettings?.(WEB_BROWSER_SETTINGS_APP_ID)) as BrowserStoredData | null) || fallback;
    } catch {
      current = fallback;
    }

    const next = updater(current || {});

    try {
      localStorage.setItem(BROWSER_DATA_LOCAL_STORAGE_KEY, JSON.stringify(next));
    } catch {
    }

    try {
      await settingsApi?.saveSettings?.(WEB_BROWSER_SETTINGS_APP_ID, next);
    } catch {
    }

    return next;
  }, [settingsApi]);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      const fallback = readFallbackData();
      try {
        const stored = ((await settingsApi?.loadSettings?.(WEB_BROWSER_SETTINGS_APP_ID)) as BrowserStoredData | null) || fallback;
        if (!isMounted) {
          return;
        }
        setBookmarks(Array.isArray(stored.bookmarks) ? stored.bookmarks : []);
        setHistoryEntries(Array.isArray(stored.history) ? stored.history : []);
        setStoredVault(stored.passwordVault || null);
      } catch {
        if (!isMounted) {
          return;
        }
        setBookmarks(Array.isArray(fallback.bookmarks) ? fallback.bookmarks : []);
        setHistoryEntries(Array.isArray(fallback.history) ? fallback.history : []);
        setStoredVault(fallback.passwordVault || null);
      } finally {
        if (isMounted) {
          setIsLoaded(true);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [settingsApi]);

  useEffect(() => {
    if (!activeTab || privateMode || activeTab.isLoading || !isHttpUrl(activeTab.url)) {
      return;
    }

    const recordKey = `${activeTab.url}|${activeTab.title}`;
    if (lastRecordedHistoryByTabRef.current[activeTab.id] === recordKey) {
      return;
    }

    lastRecordedHistoryByTabRef.current[activeTab.id] = recordKey;

    const entry: BrowserHistoryEntry = {
      id: `history-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      title: activeTab.title || getHostname(activeTab.url),
      url: activeTab.url,
      favicon: activeTab.favicon || null,
      visitedAt: new Date().toISOString(),
    };

    setHistoryEntries((current) => {
      const next = [entry, ...current].slice(0, MAX_HISTORY_ITEMS);
      persistData((stored) => ({
        ...stored,
        history: next,
      })).catch(() => {});
      return next;
    });
  }, [activeTab, persistData, privateMode]);

  const currentBookmark = useMemo(() => {
    if (!activeTab?.url) {
      return null;
    }
    return bookmarks.find((bookmark) => bookmark.url === activeTab.url) || null;
  }, [activeTab?.url, bookmarks]);

  const currentOrigin = useMemo(() => {
    if (!activeTab?.url) {
      return null;
    }
    return getOrigin(activeTab.url);
  }, [activeTab?.url]);

  const currentSitePasswords = useMemo(() => {
    if (!currentOrigin) {
      return [];
    }
    return savedPasswords.filter((entry) => entry.origin === currentOrigin);
  }, [currentOrigin, savedPasswords]);

  const toggleBookmarkForActiveTab = useCallback(async () => {
    if (!activeTab?.url || !isHttpUrl(activeTab.url)) {
      return false;
    }

    if (currentBookmark) {
      const next = bookmarks.filter((bookmark) => bookmark.id !== currentBookmark.id);
      setBookmarks(next);
      await persistData((stored) => ({
        ...stored,
        bookmarks: next,
      }));
      return false;
    }

    const nextBookmark: BrowserBookmark = {
      id: `bookmark-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      title: activeTab.title || getHostname(activeTab.url),
      url: activeTab.url,
      favicon: activeTab.favicon || null,
      createdAt: new Date().toISOString(),
    };

    const next = [nextBookmark, ...bookmarks].sort((left, right) => right.createdAt.localeCompare(left.createdAt));
    setBookmarks(next);
    await persistData((stored) => ({
      ...stored,
      bookmarks: next,
    }));
    return true;
  }, [activeTab, bookmarks, currentBookmark, persistData]);

  const removeBookmark = useCallback(async (bookmarkId: string) => {
    const next = bookmarks.filter((bookmark) => bookmark.id !== bookmarkId);
    setBookmarks(next);
    await persistData((stored) => ({
      ...stored,
      bookmarks: next,
    }));
  }, [bookmarks, persistData]);

  const clearHistory = useCallback(async () => {
    setHistoryEntries([]);
    lastRecordedHistoryByTabRef.current = {};
    await persistData((stored) => ({
      ...stored,
      history: [],
    }));
  }, [persistData]);

  const createPasswordVault = useCallback(async (masterPassword: string) => {
    const encryptedVault = await encryptPasswordVault<StoredPasswordVaultPayload>({ credentials: [] }, masterPassword);
    masterPasswordRef.current = masterPassword;
    setStoredVault(encryptedVault);
    setSavedPasswords([]);
    setIsPasswordVaultUnlocked(true);
    await persistData((stored) => ({
      ...stored,
      passwordVault: encryptedVault,
    }));
  }, [persistData]);

  const unlockPasswordVault = useCallback(async (masterPassword: string) => {
    if (!storedVault) {
      throw new Error('Password vault is not set up yet.');
    }
    const payload = await decryptPasswordVault<StoredPasswordVaultPayload>(storedVault, masterPassword);
    masterPasswordRef.current = masterPassword;
    setSavedPasswords(Array.isArray(payload.credentials) ? payload.credentials : []);
    setIsPasswordVaultUnlocked(true);
  }, [storedVault]);

  const lockPasswordVault = useCallback(() => {
    masterPasswordRef.current = null;
    setSavedPasswords([]);
    setIsPasswordVaultUnlocked(false);
  }, []);

  const persistPasswords = useCallback(async (nextPasswords: BrowserSavedPassword[]) => {
    const masterPassword = masterPasswordRef.current;
    if (!masterPassword) {
      throw new Error('Unlock the password vault first.');
    }
    const encryptedVault = await encryptPasswordVault<StoredPasswordVaultPayload>({ credentials: nextPasswords }, masterPassword);
    setStoredVault(encryptedVault);
    setSavedPasswords(nextPasswords);
    await persistData((stored) => ({
      ...stored,
      passwordVault: encryptedVault,
    }));
  }, [persistData]);

  const savePassword = useCallback(async ({
    origin,
    username,
    password,
    label,
  }: {
    origin: string;
    username: string;
    password: string;
    label?: string;
  }) => {
    const normalizedOrigin = getOrigin(origin);
    if (!normalizedOrigin) {
      throw new Error('Passwords can only be saved for http(s) websites.');
    }

    const trimmedUsername = username.trim();
    const trimmedPassword = password.trim();
    if (!trimmedUsername || !trimmedPassword) {
      throw new Error('Username and password are required.');
    }

    const now = new Date().toISOString();
    const existing = savedPasswords.find(
      (entry) => entry.origin === normalizedOrigin && entry.username.toLowerCase() === trimmedUsername.toLowerCase()
    );

    const nextEntry: BrowserSavedPassword = existing
      ? {
          ...existing,
          label: label?.trim() || existing.label,
          password: trimmedPassword,
          updatedAt: now,
        }
      : {
          id: `password-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          origin: normalizedOrigin,
          label: label?.trim() || getHostname(normalizedOrigin),
          username: trimmedUsername,
          password: trimmedPassword,
          createdAt: now,
          updatedAt: now,
        };

    const nextPasswords = existing
      ? savedPasswords.map((entry) => (entry.id === existing.id ? nextEntry : entry))
      : [nextEntry, ...savedPasswords];

    await persistPasswords(nextPasswords);
    return nextEntry;
  }, [persistPasswords, savedPasswords]);

  const deletePassword = useCallback(async (passwordId: string) => {
    const nextPasswords = savedPasswords.filter((entry) => entry.id !== passwordId);
    await persistPasswords(nextPasswords);
  }, [persistPasswords, savedPasswords]);

  const fillPassword = useCallback(async (entry: BrowserSavedPassword) => {
    if (!browserApi || !activeTab) {
      return false;
    }
    return browserApi.fillCredentials({
      hostId: windowId,
      tabId: activeTab.id,
      username: entry.username,
      password: entry.password,
    });
  }, [activeTab, browserApi, windowId]);

  return {
    isLoaded,
    bookmarks,
    historyEntries,
    currentBookmark,
    toggleBookmarkForActiveTab,
    removeBookmark,
    clearHistory,
    currentOrigin,
    hasPasswordVault: Boolean(storedVault),
    isPasswordVaultUnlocked,
    savedPasswords,
    currentSitePasswords,
    createPasswordVault,
    unlockPasswordVault,
    lockPasswordVault,
    savePassword,
    deletePassword,
    fillPassword,
  };
};
